"use client"

import { useState, useMemo, useCallback, useEffect } from "react"
import { Search, ChevronLeft, ChevronRight, Edit2, Trash2, Plus, X } from "lucide-react"
import { toast } from "react-toastify"
import {
  Container,
  Sidebar,
  SidebarHeader,
  SidebarTitle,
  SidebarSubtitle,
  SidebarSection,
  SidebarLabel,
  Select,
  ScreenList,
  ScreenButton,
  MainContent,
  ContentWrapper,
  Header,
  PageTitle,
  PageDescription,
  MetricsGrid,
  MetricCard,
  MetricLabel,
  MetricValue,
  SearchContainer,
  SearchInput,
  SearchIconWrapper,
  TabsContainer,
  TabButton,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableCell,
  KeyCell,
  EmptyText,
  StatusBadge,
  PaginationContainer,
  PaginationButton,
  PaginationInfo,
  EmptyState,
  LoadingContainer,
  LoadingContent,
  Spinner,
  LoadingText,
  SidebarSearchContainer,
  SidebarSearchInput,
  SidebarSearchIcon,
  CreateButton,
  ActionButton,
  ActionButtonsContainer,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  FormGroup,
  FormLabel,
  FormInput,
  FormSelect,
  FormInputGroup,
  FormButtonGroup,
  Button,
  ModalText,
  SearchAndCreateContainer,
  TableActionHeader,
} from "./translation-gestor.styled"
import {
  getGestorTranslationPage,
  getScreenTranslations,
  createTranslation,
  updateTranslation,
  deleteTranslation,
} from "../../Api/content"
import { useLocale } from "../../i18n/localeContext"
import { invalidateTranslationsCache } from "./translateValue"

export default function GestorTraducciones() {
  // State management
  const [selectedScreen, setSelectedScreen] = useState("")
  const [availableScreens, setAvailableScreens] = useState([])
  const [activeTab, setActiveTab] = useState("translations")
  const [searchTerm, setSearchTerm] = useState("")
  const [screenSearch, setScreenSearch] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [selectedLanguage, setSelectedLanguage] = useState("en")
  const [availableLanguages, setAvailableLanguages] = useState([
    { code: "es", label: "Español (ES)" },
    { code: "en", label: "Inglés (EN)" },
  ])
  const [translations, setTranslations] = useState([])
  const [missing, setMissing] = useState([])
  const [isLoading, setIsLoadingContent] = useState(true)
  const [content, setContent] = useState(null)
  const { locale } = useLocale()

  // Modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [currentTranslation, setCurrentTranslation] = useState(null)
  const [isCreatingNewScreen, setIsCreatingNewScreen] = useState(false)

  // Form states
  const [formData, setFormData] = useState({
    screen: "",
    key: "",
    es: "",
    [selectedLanguage]: "",
  })

  const itemsPerPage = 5

  // Function to calculate translation status
  const calculateTranslationStatus = useCallback((translation, selectedLang) => {
    return translation.es && translation[selectedLang] ? "complete" : "missing"
  }, [])

  // Function to transform API data to component format
  const transformApiData = useCallback(
    (apiData, selectedLang) => {
      if (!apiData?.data) return { screens: [], translations: [], missing: [], languages: [] }

      const screens = apiData.data.screens || []
      const apiTranslations = apiData.data.translations || []

      // Detect dynamic languages from translations
      const detectedLanguages = new Set()
      detectedLanguages.add("es")
      apiTranslations.forEach((t) => {
        Object.keys(t).forEach((key) => {
          if (key !== "id" && key !== "screen" && key !== "key" && typeof t[key] === "string") {
            detectedLanguages.add(key)
          }
        })
      })

      const labels = {
        es: "Español (ES)",
        en: "Inglés (EN)",
        de: "Alemán (DE)",
        fr: "Francés (FR)",
        it: "Italiano (IT)",
        pt: "Portugués (PT)",
      }
      const languages = Array.from(detectedLanguages).map((code) => ({
        code,
        label: labels[code] || `${code.toUpperCase()}`,
      }))

      // Transform API translations to component format
      const transformedTranslations = apiTranslations.map((t) => ({
        id: t.id,
        screen: t.screen,
        key: t.key,
        es: t.es,
        en: t.en,
        ...Object.keys(t).reduce((acc, key) => {
          if (key !== "id" && key !== "screen" && key !== "key" && typeof t[key] === "string") {
            acc[key] = t[key]
          }
          return acc
        }, {}),
        status: calculateTranslationStatus(t, selectedLang),
      }))

      const missingTranslations = []

      return { screens, translations: transformedTranslations, missing: missingTranslations, languages }
    },
    [calculateTranslationStatus],
  )

  // Load translations from API on mount (only once)
  useEffect(() => {
    let cancel = false

    getScreenTranslations()
      .then((translationsResponse) => {
        if (!cancel) {
          // Process translations
          if (translationsResponse?.responseStatus === "Success" && translationsResponse?.data) {
            const transformed = transformApiData(translationsResponse, selectedLanguage)

            setAvailableScreens(transformed.screens)
            setTranslations(transformed.translations)
            setMissing(transformed.missing)

            if (transformed.languages && transformed.languages.length > 0) {
              setAvailableLanguages(transformed.languages)
              if (!transformed.languages.find((l) => l.code === selectedLanguage)) {
                setSelectedLanguage(transformed.languages[0].code)
              }
            }

            if (transformed.screens.length > 0) {
              setSelectedScreen(transformed.screens[0])
            }
          }
        }
      })
      .catch(() => {
        // Handle error silently
      })

    return () => {
      cancel = true
    }
  }, [])

  // Load content based on current locale
  useEffect(() => {
    let cancel = false;
    setIsLoadingContent(true);

    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setContent(null);
        setIsLoadingContent(false);
      }
    }, 3000);

    getGestorTranslationPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setContent(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setContent(null);
          setIsLoadingContent(false);
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  // Update statuses when language changes
  useEffect(() => {
    setTranslations((prev) =>
      prev.map((t) => ({
        ...t,
        status: calculateTranslationStatus(t, selectedLanguage),
      })),
    )
  }, [selectedLanguage, calculateTranslationStatus])

  // Filter screens by search
  const filteredScreens = useMemo(() => {
    if (!screenSearch.trim()) return availableScreens
    const searchLower = screenSearch.toLowerCase()
    return availableScreens.filter((screen) => screen.toLowerCase().includes(searchLower))
  }, [availableScreens, screenSearch])

  // Filter translations by screen and search
  const filteredTranslations = useMemo(() => {
    const allKeys = new Set()

    translations.filter((t) => t.screen === selectedScreen).forEach((t) => allKeys.add(t.key))

    missing.filter((m) => m.screen === selectedScreen).forEach((m) => allKeys.add(m.key))

    const allTranslations = Array.from(allKeys).map((key) => {
      const existing = translations.find((t) => t.screen === selectedScreen && t.key === key)

      if (existing) {
        return existing
      }

      const missingItem = missing.find((m) => m.screen === selectedScreen && m.key === key)

      if (missingItem) {
        return {
          id: missingItem.id,
          screen: missingItem.screen,
          key: missingItem.key,
          es: missingItem.baseValue,
          [selectedLanguage]: undefined,
          status: "missing",
        }
      }

      return {
        id: Date.now(),
        screen: selectedScreen,
        key,
        es: undefined,
        [selectedLanguage]: undefined,
        status: "missing",
      }
    })

    const searchLower = searchTerm.toLowerCase()
    return allTranslations.filter((t) => {
      const selectedLangValue = t[selectedLanguage]
      const matchesSearch =
        !searchLower ||
        t.key.toLowerCase().includes(searchLower) ||
        (t.es && t.es.toLowerCase().includes(searchLower)) ||
        (selectedLangValue && String(selectedLangValue).toLowerCase().includes(searchLower))
      return matchesSearch
    })
  }, [selectedScreen, searchTerm, translations, missing, selectedLanguage])

  // Calculate metrics
  const totalKeys = filteredTranslations.length
  const completeCount = useMemo(() => {
    return filteredTranslations.filter((t) => t.es && t[selectedLanguage]).length
  }, [filteredTranslations, selectedLanguage])
  const missingCount = totalKeys > 0 ? totalKeys - completeCount : 0
  const coverage = totalKeys > 0 ? Math.round((completeCount / totalKeys) * 100) : 0

  // Get filtered missing translations
  const filteredMissing = useMemo(() => {
    if (!selectedScreen) return []

    const missingFromList = missing.filter((m) => m.screen === selectedScreen)

    const translationsWithoutSelectedLang = translations
      .filter((t) => t.screen === selectedScreen && t.es && !t[selectedLanguage])
      .map((t) => ({
        id: t.id,
        screen: t.screen,
        key: t.key,
        baseValue: t.es || "",
      }))

    return [...missingFromList, ...translationsWithoutSelectedLang]
  }, [selectedScreen, missing, translations, selectedLanguage])

  // Get selected language label
  const selectedLanguageLabel =
    availableLanguages.find((lang) => lang.code === selectedLanguage)?.label || selectedLanguage.toUpperCase()

  // Separate by tab
  const displayedTranslations = useMemo(() => {
    if (activeTab === "translations") {
      return filteredTranslations.filter((t) => t.status === "complete")
    } else {
      return filteredTranslations.filter((t) => t.status === "missing")
    }
  }, [filteredTranslations, activeTab])

  // Paginate
  const totalPages = Math.ceil(displayedTranslations.length / itemsPerPage)
  const paginatedTranslations = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    return displayedTranslations.slice(startIndex, startIndex + itemsPerPage)
  }, [displayedTranslations, currentPage])

  // Handlers
  const handleTabChange = (tab) => {
    setActiveTab(tab)
    setCurrentPage(1)
  }

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value)
    setCurrentPage(1)
  }

  const handleScreenChange = (screen) => {
    setSelectedScreen(screen)
    setCurrentPage(1)
  }

  const handleOpenCreateModal = () => {
    setIsCreateModalOpen(true)
    setIsCreatingNewScreen(false)
    setFormData({
      screen: selectedScreen,
      key: "",
      es: "",
      [selectedLanguage]: "",
    })
  }

  const handleOpenEditModal = useCallback(
    (record) => {
      setCurrentTranslation(record)
      setIsEditModalOpen(true)
      setFormData({
        screen: record.screen,
        key: record.key,
        es: record.es || "",
        [selectedLanguage]: record[selectedLanguage] || "",
      })
    },
    [selectedLanguage],
  )

  const handleOpenDeleteModal = (record) => {
    setCurrentTranslation(record)
    setIsDeleteModalOpen(true)
  }

  const handleCloseModals = () => {
    setIsEditModalOpen(false)
    setIsCreateModalOpen(false)
    setIsDeleteModalOpen(false)
    setCurrentTranslation(null)
    setIsCreatingNewScreen(false)
    setFormData({
      screen: "",
      key: "",
      es: "",
      [selectedLanguage]: "",
    })
  }

  const handleSaveTranslation = async (e) => {
    e.preventDefault()

    try {
      if (isEditModalOpen && currentTranslation) {
        // Update existing translation
        const traducciones = {}

        if (formData.es) {
          traducciones.es = formData.es
        }

        if (formData[selectedLanguage]) {
          traducciones[selectedLanguage] = formData[selectedLanguage]
        }

        const response = await updateTranslation(currentTranslation.screen, currentTranslation.key, traducciones)

        if (response?.responseStatus === "Success") {
          // Invalidar el caché después de actualizar
          invalidateTranslationsCache()
          const updatedTranslations = await getScreenTranslations()
          if (updatedTranslations?.responseStatus === "Success" && updatedTranslations?.data) {
            const transformed = transformApiData(updatedTranslations, selectedLanguage)
            setTranslations(transformed.translations)
            setMissing(transformed.missing)
          }
          toast.success(content?.translation_updated || "Traducción actualizada correctamente")
          handleCloseModals()
        } else {
          toast.error(response?.message || content?.error_update_translation || "Error al actualizar la traducción")
        }
      } else {
        // Create new translation
        const traducciones = {}

        if (formData.es) {
          traducciones.es = formData.es
        }

        if (formData[selectedLanguage]) {
          traducciones[selectedLanguage] = formData[selectedLanguage]
        }

        const response = await createTranslation({
          key: formData.key,
          screen: formData.screen,
          traducciones: traducciones,
        })

        if (response?.responseStatus === "Success") {
          // Invalidar el caché después de crear
          invalidateTranslationsCache()
          const updatedTranslations = await getScreenTranslations()
          if (updatedTranslations?.responseStatus === "Success" && updatedTranslations?.data) {
            const transformed = transformApiData(updatedTranslations, selectedLanguage)
            setTranslations(transformed.translations)
            setMissing(transformed.missing)

            // Add new screen if it doesn't exist
            if (formData.screen && !availableScreens.includes(formData.screen)) {
              setAvailableScreens((prev) => [...prev, formData.screen])
              setSelectedScreen(formData.screen)
            }
          }
          toast.success(content?.translation_created || "Traducción creada correctamente")
          handleCloseModals()
        } else {
          toast.error(response?.message || content?.error_create_translation || "Error al crear la traducción")
        }
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || content?.error_create_translation || "Error al procesar la traducción")
    }
  }

  const handleDeleteTranslation = async () => {
    if (!currentTranslation) return

    try {
      const response = await deleteTranslation(currentTranslation.screen, currentTranslation.key)

      if (response?.responseStatus === "Success") {
        // Invalidar el caché después de eliminar
        invalidateTranslationsCache()
        const updatedTranslations = await getScreenTranslations()
        if (updatedTranslations?.responseStatus === "Success" && updatedTranslations?.data) {
          const transformed = transformApiData(updatedTranslations, selectedLanguage)
          setTranslations(transformed.translations)
          setMissing(transformed.missing)
        }
        toast.success(content?.translation_deleted || "Traducción eliminada correctamente")
        handleCloseModals()
      } else {
        toast.error(response?.message || content?.error_delete_translation || "Error al eliminar la traducción")
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || content?.error_delete_translation || "Error al eliminar la traducción")
    }
  }

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingContent>
          <Spinner />
          <LoadingText>Cargando traducciones...</LoadingText>
        </LoadingContent>
      </LoadingContainer>
    )
  }

  return (
    <Container>
      {/* Sidebar */}
      <Sidebar>
        <SidebarHeader>
          <SidebarTitle>{content?.sb_title || "Gestor de Traducciones"}</SidebarTitle>
          <SidebarSubtitle>{content?.sb_description || "Gestiona tus traducciones"}</SidebarSubtitle>
        </SidebarHeader>

        {/* Current Language */}
        <SidebarSection>
          <SidebarLabel>{content?.sb_language || "Idioma actual"}</SidebarLabel>
          <Select value={selectedLanguage} onChange={(e) => setSelectedLanguage(e.target.value)}>
            {availableLanguages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.label}
              </option>
            ))}
          </Select>
        </SidebarSection>

        {/* Screens */}
        <SidebarSection>
          <SidebarLabel>{content?.sb_screen || "PANTALLAS"}</SidebarLabel>
          <SidebarSearchContainer>
            <SidebarSearchIcon>
              <Search size={14} width={14}/>
            </SidebarSearchIcon>
            <SidebarSearchInput
              type="text"
              placeholder={content?.search_spaceholder || "Buscar pantalla..."}
              value={screenSearch}
              onChange={(e) => setScreenSearch(e.target.value)}
            />
          </SidebarSearchContainer>
          <ScreenList style={{ maxHeight: '384px', overflowY: 'auto' }}>
            {filteredScreens.map((screen) => (
              <li key={screen}>
                <ScreenButton $active={selectedScreen === screen} onClick={() => handleScreenChange(screen)}>
                  {screen}
                </ScreenButton>
              </li>
            ))}
          </ScreenList>
        </SidebarSection>
      </Sidebar>

      {/* Main Content */}
      <MainContent>
        <ContentWrapper>
          {/* Header */}
          <Header>
            <PageTitle>
              {content?.title || "Gestor de Traducciones"} {selectedScreen}
            </PageTitle>
            <PageDescription>
              {content?.description ||
                "Revisa las traducciones, filtra por texto y controla qué frases todavía faltan por traducir."}
            </PageDescription>
          </Header>

          {/* Search and Create Button */}
          <SearchAndCreateContainer>
            <SearchContainer style={{ flex: 1 }}>
              <SearchIconWrapper>
                <Search size={16}/>
              </SearchIconWrapper>
              <SearchInput
                type="text"
                placeholder={content?.search_input || "Buscar por key o texto (Ej: login, estudiante, BTN_SAVE...)"}
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </SearchContainer>
            <CreateButton onClick={handleOpenCreateModal}>
              <Plus size={20} />
              {content?.new_translate || "Crear traducción"}
            </CreateButton>
          </SearchAndCreateContainer>

          {/* Dashboard Cards */}
          <MetricsGrid>
            <MetricCard>
              <MetricLabel>{content?.key_dashboard || "Keys en pantalla"}</MetricLabel>
              <MetricValue>{totalKeys}</MetricValue>
            </MetricCard>
            <MetricCard>
              <MetricLabel>{content?.translate_dashboard || "Traducciones completas"}</MetricLabel>
              <MetricValue>{completeCount}</MetricValue>
            </MetricCard>
            <MetricCard $warning={coverage < 100} $success={coverage === 100}>
              <MetricLabel>
                {content?.cover_language_dashboard || "Cobertura en"} {selectedLanguageLabel.toLowerCase()}{" "}
                <span style={{ fontSize: '12px', fontWeight: 'normal' }}>
                  ({missingCount} {content?.pending_dashboard || "pendientes por traducir"})
                </span>
              </MetricLabel>
              <MetricValue>{coverage}%</MetricValue>
            </MetricCard>
          </MetricsGrid>

          {/* Tabs */}
          <TabsContainer>
            <TabButton $active={activeTab === "translations"} onClick={() => handleTabChange("translations")}>
              {content?.translate_button || "Traducciones"}
            </TabButton>
            <TabButton $active={activeTab === "missing"} onClick={() => handleTabChange("missing")}>
              {content?.pending_translate_button || "Pendientes por traducir"}
            </TabButton>
          </TabsContainer>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <tr>
                  <TableHeader>{content?.translate_table?.[0]?.label || "Key"}</TableHeader>
                  <TableHeader>{content?.translate_table?.[1]?.label || "Español"}</TableHeader>
                  <TableHeader>{selectedLanguageLabel}</TableHeader>
                  <TableHeader>{content?.translate_table?.[3]?.label || "Estado"}</TableHeader>
                  <TableActionHeader>Acciones</TableActionHeader>
                </tr>
              </TableHead>
              <TableBody>
                {paginatedTranslations.length > 0 ? (
                  paginatedTranslations.map((translation) => (
                    <tr key={translation.id}>
                      <KeyCell>{translation.key}</KeyCell>
                      <TableCell>{translation.es || <EmptyText>—</EmptyText>}</TableCell>
                      <TableCell>
                        {translation[selectedLanguage] || <EmptyText>{content?.no_translate || "Sin traducción"}</EmptyText>}
                      </TableCell>
                      <TableCell>
                        <StatusBadge $status={translation.status}>
                          {translation.status === "complete"
                            ? content?.status_complete || "Completa"
                            : content?.status_missing || "Faltante"}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        <ActionButtonsContainer>
                          <ActionButton
                            $variant="edit"
                            onClick={() => handleOpenEditModal(translation)}
                            title={content?.edit_translate || "Editar"}
                          >
                            <Edit2 size={16} />
                          </ActionButton>
                          <ActionButton
                            $variant="delete"
                            onClick={() => handleOpenDeleteModal(translation)}
                            title={content?.delete_button || "Eliminar"}
                          >
                            <Trash2 size={16} />
                          </ActionButton>
                        </ActionButtonsContainer>
                      </TableCell>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <EmptyState colSpan="5">
                      {activeTab === "translations"
                        ? content?.empty_text_translations ||
                          "No hay traducciones para esta pantalla o criterio de búsqueda."
                        : content?.empty_text_missing || "No hay frases pendientes para esta pantalla 🎉"}
                    </EmptyState>
                  </tr>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <PaginationContainer>
              <PaginationButton
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={20} />
              </PaginationButton>

              <PaginationInfo>
                Página <strong>{currentPage}</strong> de <strong>{totalPages}</strong>
              </PaginationInfo>

              <PaginationButton
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={20} />
              </PaginationButton>

              <Select value={itemsPerPage} disabled style={{ width: 'auto', minWidth: '120px' }}>
                <option value={5}>5 / página</option>
              </Select>
            </PaginationContainer>
          )}
        </ContentWrapper>
      </MainContent>

      {/* Create/Edit Modal */}
      {(isCreateModalOpen || isEditModalOpen) && (
        <ModalOverlay onClick={handleCloseModals}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {isEditModalOpen
                  ? content?.edit_translate || "Editar traducción"
                  : content?.new_translate || "Crear traducción"}
              </ModalTitle>
              <ModalCloseButton onClick={handleCloseModals}>
                <X size={20} />
              </ModalCloseButton>
            </ModalHeader>

            <form onSubmit={handleSaveTranslation}>
              {/* Screen field */}
              <FormGroup>
                <FormLabel>{content?.sb_screen || "Pantalla"}</FormLabel>
                {isCreatingNewScreen ? (
                  <FormInputGroup>
                    <FormInput
                      type="text"
                      value={formData.screen}
                      onChange={(e) => setFormData({ ...formData, screen: e.target.value })}
                      placeholder={content?.new_screen_placeholder || "Ingresa el nombre de la nueva pantalla"}
                      disabled={isEditModalOpen}
                      required
                    />
                    <Button
                      type="button"
                      onClick={() => {
                        setIsCreatingNewScreen(false)
                        setFormData({ ...formData, screen: selectedScreen })
                      }}
                      disabled={isEditModalOpen}
                    >
                      <X size={20} />
                    </Button>
                  </FormInputGroup>
                ) : (
                  <FormInputGroup>
                    <FormSelect
                      value={formData.screen}
                      onChange={(e) => setFormData({ ...formData, screen: e.target.value })}
                      disabled={isEditModalOpen}
                      required
                      style={{ flex: 1 }}
                    >
                      <option value="">{content?.select_option || "Selecciona una pantalla"}</option>
                      {availableScreens.map((screen) => (
                        <option key={screen} value={screen}>
                          {screen}
                        </option>
                      ))}
                    </FormSelect>
                    {!isEditModalOpen && (
                      <Button
                        type="button"
                        onClick={() => {
                          setIsCreatingNewScreen(true)
                          setFormData({ ...formData, screen: "" })
                        }}
                      >
                        <Plus size={20} />
                      </Button>
                    )}
                  </FormInputGroup>
                )}
              </FormGroup>

              {/* Key field */}
              <FormGroup>
                <FormLabel>Key</FormLabel>
                <FormInput
                  type="text"
                  value={formData.key}
                  onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                  placeholder="Ej: BTN_SAVE, LBL_NAME, etc."
                  disabled={isEditModalOpen}
                  required
                />
              </FormGroup>

              {/* Spanish field */}
              <FormGroup>
                <FormLabel>{content?.translate_table?.[1]?.label || "Español"}</FormLabel>
                <FormInput
                  type="text"
                  value={formData.es}
                  onChange={(e) => setFormData({ ...formData, es: e.target.value })}
                  placeholder={content?.text_input || "Ingresa el texto"}
                  required
                />
              </FormGroup>

              {/* Selected language field */}
              <FormGroup>
                <FormLabel>{selectedLanguageLabel}</FormLabel>
                <FormInput
                  type="text"
                  value={formData[selectedLanguage]}
                  onChange={(e) => setFormData({ ...formData, [selectedLanguage]: e.target.value })}
                  placeholder={content?.text_input || "Ingresa el texto"}
                  required
                />
              </FormGroup>

              {/* Buttons */}
              <FormButtonGroup>
                <Button type="button" onClick={handleCloseModals}>
                  {content?.cancel_button || "Cancelar"}
                </Button>
                <Button type="submit" $variant="primary">
                  {isEditModalOpen ? content?.save_button || "Guardar" : content?.create_button || "Crear"}
                </Button>
              </FormButtonGroup>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && currentTranslation && (
        <ModalOverlay onClick={handleCloseModals}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle style={{ marginBottom: '16px' }}>
              {content?.delete_confirmation_title || "¿Estás seguro de eliminar esta traducción?"}
            </ModalTitle>
            <ModalText>
              {content?.delete_confirmation || "Se eliminará la traducción con key"} "{currentTranslation.key}"
            </ModalText>
            <FormButtonGroup>
              <Button onClick={handleCloseModals}>
                {content?.cancel_button || "Cancelar"}
              </Button>
              <Button onClick={handleDeleteTranslation} $variant="danger">
                {content?.delete_button || "Eliminar"}
              </Button>
            </FormButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  )
}